import dayjs                                               from "dayjs";
import duration                                            from "dayjs/plugin/duration";
import { t }                                               from "i18next";
import { debounce }                                        from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";

import useTimer                                                       from "_common/custom-hooks/timer";
import { useGetLoggedIn, useGetProStatus, useGetProfileData }         from "_common/hooks/auth";
import { useGetIsFollowStatus, useSetLoginPopup }                     from "_common/hooks/global";
import Toast                                                          from "_common/toast";
import { getBaseURL }                                                 from "_common/utils";
import { useGetDynamicData, useGetHasUserDonated, useGetIsModerator } from "_components/features/ivsChat/states";
import { useGetUpdateSession }                                        from "_jotai/liveSession";
import { getBroadcastSessionAPI }                                     from "_services/api/broadcast";
import { addCommentOnBroadcast }                                      from "_services/api/engagement";
import { getTagsForComments }                                         from "_services/api/livestream";

import Presentation from "./presentation";

dayjs.extend ( duration );

const MINUTES_IN_HOUR              = 60;
const WAITING_MINUTES_AFTER_FOLLOW = 0.5;

const ChatInputField = ( {
	broadcastId,
	closeEmojiModal,
	emotesList,
	handleCommentOptimistic,
	handleModal,
	isSessionEnded,
	isStreamer,
	myMutedStateData,
	pollsCount,
	showPolls,
	togglePolls,
	handleDisabledChatInputClick
} ) => {

	const initDisableChatData = {
		tooltipText: ""
	};

	const prevRemSecs           = useRef ( 0 );
	const textInput             = useRef ( null );
	const userProfile           = useGetProfileData ();
	const isFollowStatus        = useGetIsFollowStatus ();
	const setLoginPopup         = useSetLoginPopup ();
	const sessionDataFromServer = useGetUpdateSession ();
	const sessionDataFromSocket = useGetDynamicData ();
	const isLoggedIn            = useGetLoggedIn ();
	const isModerator           = useGetIsModerator ();
	const isProUser             = useGetProStatus ();
	const hasUserDonated        = useGetHasUserDonated ();

	const broadcasterName = sessionDataFromServer?.broadcaster?.sportsFan?.name;
	const endTimeUTC      = sessionDataFromSocket?.endTimeUTC;

	const enableDonorOnly = (
		sessionDataFromServer?.donorOnlyChat &&
    !isModerator && !isProUser &&
    !sessionDataFromServer?.hasUserDonated
	) ? true : false;

	// sessionDataBroadcasterId
	// const isStreamer = userProfile?.id === sessionDataBroadcasterId ? true : false;
	const [ inputLength, setInputLength ]           = useState ( 0 );
	const [ followerOnlyChat, setFollowerOnlyChat ] = useState ( sessionDataFromServer?.followerOnlyChat ? true : false );
	const [ donorOnlyChat, setDonorOnlyChat ]       = useState ( enableDonorOnly );
	const [ followingStatus, setFollowingStatus ]   = useState ( {} );
	const [ remainingSeconds, setRemainingSeconds ] = useState ( 0 );
	const [ disableChat, setDisableChat ]           = useState ( !isStreamer );
	const [ showTooltipInfoIcon, setToolTipIcon ]   = useState ( !isStreamer );
	const [ disableChatData, setDisableChatData ]   = useState ( initDisableChatData );
	const [ focusState, setFocusState ]             = useState ( false );
	const [ displayEmoji, setDisplayEmoji ]         = useState ( false );

	useEffect ( () => {
		if ( !isModerator ) {
			const enableDonorOnly = (
				sessionDataFromServer?.donorOnlyChat &&
        !isProUser && !sessionDataFromServer?.hasUserDonated
			) ? true : false;

			if ( enableDonorOnly )
				setDonorOnlyChat ( true );

			return;
		}

		setDonorOnlyChat ( false );
	}, [ isModerator ] );

	useEffect ( () => {
		if ( closeEmojiModal ) {
			setDisplayEmoji ( false );
		}
	}, [ closeEmojiModal ] );

	useEffect ( () => {
		if ( typeof sessionDataFromSocket?.followerOnlyChat === "undefined" )
			return;

		setFollowerOnlyChat ( sessionDataFromSocket?.followerOnlyChat );

	}, [ sessionDataFromSocket?.followerOnlyChat ] );

	useEffect ( () => {
		if ( typeof sessionDataFromSocket?.donorOnlyChat === "undefined" )
			return;

		if ( isProUser || isModerator || sessionDataFromServer?.hasUserDonated )
			return;

		setDonorOnlyChat ( sessionDataFromSocket?.donorOnlyChat );
	}, [ sessionDataFromSocket?.donorOnlyChat, isProUser, isModerator ] );

	useEffect ( () => {
		if ( hasUserDonated )
			setDonorOnlyChat ( false );
	}, [ hasUserDonated ] );

	useEffect ( () => {
		if ( isStreamer || !followerOnlyChat )
			return;

		fetchIsFollwer ( broadcastId );
	}, [ followerOnlyChat, isFollowStatus ] );

	const fetchIsFollwer = async id => {
		try {
			const res = await getBroadcastSessionAPI ( { broadcastSessionId: id } );

			setFollowingStatus ( res.broadcaster.sportsFan.followingStatus );
		} catch ( err ) {
			Toast.error ( err.displayMsg );
		}
	};

	const calculateRemainingSecondsToEnableChat = () => {
		if ( isStreamer )
			return;

		let remainingSeconds;

		if ( followerOnlyChat ) {
			const followAt              = followingStatus?.createdAt?.replace ( " ", "T" ) + "Z";
			const timestampWhenFollowed = dayjs ( followAt );
			const currentTime           = dayjs ( dayjs ( new Date () ) );
			const difference            = Math.round ( dayjs.duration ( currentTime.diff ( timestampWhenFollowed ) ).asSeconds () );
			const waitingSeconds        = WAITING_MINUTES_AFTER_FOLLOW * 60;

			remainingSeconds = waitingSeconds - difference;

			if ( remainingSeconds > 0 && remainingSeconds <= waitingSeconds ) {
				prevRemSecs.current = remainingSeconds;
				setRemainingSeconds ( remainingSeconds );
				// setCommentInput ( "" );
				textInput.current.value = "";
			}
			if ( !followingStatus?.isFollowing ) {
				// setCommentInput ( "" );
				if ( textInput && textInput.current ) {
					textInput.current.value = "";
				}
			}

		}

		if ( myMutedStateData?.banEndTime ) {
			if ( myMutedStateData.isUnmuted ) {
				setRemainingSeconds ( 0 );

				return;
			}
			const currentTime = dayjs ( dayjs ( new Date () ).utc () );
			const endTime     = dayjs ( myMutedStateData.banEndTime );
			const difference  = Math.round ( dayjs.duration ( endTime.diff ( currentTime ) ).asSeconds () );

			remainingSeconds = difference;

			if ( remainingSeconds > 0 ) {
				prevRemSecs.current = remainingSeconds;
				setRemainingSeconds ( remainingSeconds );
				// setCommentInput ( "" );
				textInput.current.value = "";
			}
		}
	};

	useEffect ( () => {
		calculateRemainingSecondsToEnableChat ();
	}, [ followerOnlyChat, myMutedStateData, followingStatus ] );

	/* useEffect ( () => {
    setCommentInput ( "" );
  }, [ broadcastId ] ); */

	const enableChat = () => {
		if ( isStreamer || !prevRemSecs.current )
			return;

		setRemainingSeconds ( 0 );

		if ( myMutedStateData.banEndTime || followerOnlyChat ) {
			setDisableChat ( false );
			setToolTipIcon ( false );
			setDisableChatData ( initDisableChatData );
		}
	};

	const timer = useTimer ( { initSecs: remainingSeconds, onTimerEnd: enableChat } );

	useEffect ( () => {
		disableChatInputHandler ();
	}, [ remainingSeconds, followingStatus, followerOnlyChat, donorOnlyChat ] );

	const disableChatInputHandler = () => {
		if ( isStreamer )
			return;

		let disabledChatInput   = true;
		let showTooltipInfoIcon = true;
		let placeholderText     = "Followers only mode active";
		let tooltipText         = (
			<div>
				Follow
				{" "}

				<span className = "fontBold">
					{broadcasterName}
				</span>

				{" "}
				to participate in chat.
			</div>
		);

		if ( !isSessionEnded && !followerOnlyChat ) {
			disabledChatInput   = false;
			placeholderText     = "";
			showTooltipInfoIcon = false;
			tooltipText         = "";
		}

		if ( followerOnlyChat && !remainingSeconds && followingStatus.createdAt ) {
			disabledChatInput   = false;
			showTooltipInfoIcon = false;
			tooltipText         = "";
		}

		if ( followerOnlyChat && followingStatus.createdAt && remainingSeconds > 0 ) {
			showTooltipInfoIcon = false;
		}

		if ( myMutedStateData.banEndTime && !myMutedStateData.isUnmuted ) {
			disabledChatInput   = true;
			showTooltipInfoIcon = true;
			tooltipText         = `You have been muted from the stream for ${ Math.ceil ( myMutedStateData.minutes / MINUTES_IN_HOUR ) } hour.`;
		}

		if ( donorOnlyChat ) {
			disabledChatInput   = true;
			showTooltipInfoIcon = true;
			tooltipText         = "Donors only chat active";
		}

		setDisableChat ( disabledChatInput );
		setToolTipIcon ( showTooltipInfoIcon );
		setDisableChatData ( { placeholderText, tooltipText } );
	};

	let placeholderText = disableChat ? "" : t ( "inputfield_text" );

	if ( !isStreamer ) {
		if ( followerOnlyChat ) {

			if ( !followingStatus.isFollowing ) {
				placeholderText = "Followers only mode";
			}

			if ( followingStatus.createdAt && remainingSeconds > 0 ) {
				placeholderText = `Activating in: ${ timer }`;
			}
		}

		if ( donorOnlyChat ) {
			placeholderText = "Donors only chat active";
		}

		if ( myMutedStateData.banEndTime && !myMutedStateData.isUnmuted ) {
			placeholderText = `Unlocking in: ${ timer }`;
		}
	}

	const getCommentTags = async () => {
		let params = {
			broadcastSessionId: broadcastId
		};

		try {
			let data = await getTagsForComments ( params );

			return data;
		} catch ( e ) {
			// handle error
		}

	};

	const handleKeydown = async e => {
		e.preventDefault ();

		if ( !textInput.current?.value || !broadcastId || !isLoggedIn || endTimeUTC )
			return;

		if ( e.key === "Enter" || e.key === "NumpadEnter" ) {

			const { id, name, isCeleb, photo, city, locale, shortBio } = userProfile;
			let requestId                                              = `${ Date.now () }:${ userProfile?.id } `;
			let sportsFan                                              = {
				id,
				name,
				city,
				locale,
				shortBio,
				photo,
				isCeleb
			};
			let commentTags                                            = await getCommentTags ();

			const commentData = {
				broadcastId : broadcastId,
				commentText : textInput.current.value,
				requestId,
				type        : "USER_COMMENT",
				sportsFan,
				id          : requestId,
				tags        : commentTags

			};

			handleCommentOptimistic ( { comment: commentData, type: "add" } );

			// setCommentInput ( "" );
			textInput.current.value = "";

			try {
				await addCommentOnBroadcast ( commentData );

			} catch ( error ) {
				handleCommentOptimistic ( { comment: commentData, type: "remove" } );

				Toast.error ( error.displayMsg );

			}
		}
	};
	const handleOnChange = e => {
		setInputLength ( e.target.value.length );
	};

	const trottledKeyDown   = debounce ( handleKeydown, 200 );
	const throttledOnChange = debounce ( handleOnChange, 200 );

	const handleSendClick = async e => { // endTimeUTC

		if ( !textInput.current.value || !broadcastId || !isLoggedIn || endTimeUTC || disableChat )
			return;

		const { id, name, isCeleb, photo, city, locale, shortBio } = userProfile;
		let requestId                                              = `${ Date.now () }:${ userProfile?.id } `;
		let sportsFan                                              = {
			id,
			name,
			city,
			locale,
			shortBio,
			photo,
			isCeleb
		};
		let commentTags                                            = await getCommentTags ();

		e.preventDefault ();

		const commentData = {
			broadcastId : broadcastId,
			commentText : textInput.current.value,
			requestId,
			type        : "USER_COMMENT",
			sportsFan,
			id          : requestId,
			tags        : commentTags
		};

		handleCommentOptimistic ( { comment: commentData, type: "add" } );
		textInput.current.value = "";
		// setCommentInput ( "" );
		try {
			await addCommentOnBroadcast ( commentData );
		} catch ( error ) {
			Toast.error ( error.displayMsg );
			handleCommentOptimistic ( { comment: commentData, type: "remove" } );
		}
	};

	/* const handleOnChange = e => {
    if ( !isLoggedIn || endTimeUTC )
      return;
    textInput.current.value = "";
    // setCommentInput ( e.target.value );
  }; */

	const handleLogin = () => {
		if ( !isLoggedIn )
			setLoginPopup ( true );
	};

	const handleEmoteClick = emote => {
		if ( disableChat ) {
			return;
		}
		if ( !isLoggedIn ) {
			setLoginPopup ( true );

			return;
		}

		let text = "";

		text                     = `:${ emote }:`;
		textInput.current.value += " " + text;
		// setCommentInput ( commentInput + " " +text );
	};

	/* For Showing Emoji in text */
	const handleEmojiSelect = useCallback (
		e => {
			if ( disableChat ) {
				return;
			}
			/*
      Splitting the id  on (‘-’), before adding ‘0x’ to each element
      */
			let sym        = e.unified.split ( "-" );
			let codesArray = [];

			/*
        Here, unified is in Unicode (hex) so
        we need to add ‘0x’ in the front of the code for it to become an emoji
        */
			sym.forEach ( el => codesArray.push ( "0x" + el ) );
			let emoji = String.fromCodePoint ( ...codesArray );

			textInput.current.value += " " + emoji;
			// setCommentInput ( commentInput + " " +emoji );
			textInput?.current?.focus ();
			setFocusState ( true );
		},
		[ textInput.current?.value, disableChat, focusState, textInput ]
	);

	/* For Toggle Emoji Container */
	const handleToggleEmoji = () => {
		if ( !isLoggedIn ) {
			setLoginPopup ( true );

			return;
		}
		if ( displayEmoji && focusState )
			textInput?.current?.focus ();
		setDisplayEmoji ( prev => !prev );
	};

	const toggleFocusState = () => {
		if ( displayEmoji && focusState )
			textInput?.current?.focus ();
		setFocusState ( prev => !prev );
	};

	const popOut = () => {
		/*
     * This feature opens up the chat container
     * in a popout window of the browser
     */
		window.open (
			`${ getBaseURL () }/popOutChat/${ broadcastId }`,
			"popUpWindow",
			`height=500, width=400, left=100, top=100, 
			resizable=yes, scrollbars=yes, toolbar=yes, menubar=no,location=no,directories=no, status=yes`
		);

		return false;
	};

	return (
		<Presentation
			avatarImage = { userProfile && userProfile.photo }
			broadcastId = { broadcastId }
			chatMode = { donorOnlyChat ? "donorOnly" : ( followerOnlyChat ? "followerOnly" : "all" ) }
			disabled = { disableChat }
			displayEmoji = { displayEmoji }
			emotesList = { emotesList }
			focusState = { focusState }
			handleDisabledChatInputClick = { handleDisabledChatInputClick }
			handleEmojiSelect = { handleEmojiSelect }
			handleEmoteClick = { handleEmoteClick }
			handleLogin = { handleLogin }
			handleModal = { handleModal }
			handleOnChange = { throttledOnChange }
			handleSendClick = { handleSendClick }
			handleToggleEmoji = { handleToggleEmoji }
			inputLength = { inputLength || textInput.current?.value.length }
			isLoggedIn = { isLoggedIn }
			isStreamer = { isStreamer }
			placeholderText = { placeholderText }
			pollsCount = { pollsCount }
			popOut = { popOut }
			showPolls = { showPolls }
			showTooltipInfoIcon = { showTooltipInfoIcon }
			textInput = { textInput }
			toggleFocusState = { toggleFocusState }
			togglePolls = { togglePolls }
			tooltipText = { disableChatData.tooltipText }
			trottledKeyDown = { trottledKeyDown }
		/>
	);
};

export default React.memo ( ChatInputField );
